import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore } from "../api/token";
import { useSession } from "./useSession";

type LaunchTypeEventType =
  | "lt.updated"
  | "lt.deleted"
  | "lt-day.created"
  | "lt-day.updated"
  | "lt-day.deleted"
  | "lt-card.created"
  | "lt-card.updated"
  | "lt-card.moved"
  | "lt-card.deleted";

interface LaunchTypeEvent {
  type: LaunchTypeEventType;
  launchId: string; // scope key, `launch-type:<id>` — legacy name
  entityId: string;
  by: { id: string; email: string | null; name: string | null };
  at: string;
  data?: unknown;
}

// Long-lived SSE на /api/v1/launch-types/:id/events — каждая мутация шаблона
// (day create/update/delete, card create/update/move/delete, patch самого типа)
// прилетает здесь, и мы инвалидируем кэши ["launchType", id] + ["launchTypes"].
// Self-events не игнорируются: клиент уже инвалидировал свой кэш через onSettled
// мутации, но повторная инвалидация дёшёвая и даёт свежий updatedAt.
export function useLaunchTypeEvents(launchTypeId: string | null | undefined): void {
  const qc = useQueryClient();
  const session = useSession();
  const currentUserId = session.data?.id ?? null;
  const userIdRef = useRef(currentUserId);
  userIdRef.current = currentUserId;

  const token = useSyncExternalStore(
    tokenStore.subscribe,
    () => tokenStore.get(),
    () => null,
  );

  useEffect(() => {
    if (!launchTypeId) return;
    if (!token && import.meta.env.PROD) return;

    const url = token
      ? `/api/v1/launch-types/${launchTypeId}/events?access_token=${encodeURIComponent(token)}`
      : `/api/v1/launch-types/${launchTypeId}/events`;
    const source = new EventSource(url, { withCredentials: false });

    const handle = (raw: MessageEvent) => {
      let event: LaunchTypeEvent;
      try {
        event = JSON.parse(raw.data);
      } catch {
        return;
      }
      // Skip self: мутация сама инвалидирует через onSettled.
      if (event.by.id && userIdRef.current && event.by.id === userIdRef.current) {
        return;
      }
      qc.invalidateQueries({ queryKey: ["launchType", launchTypeId] });
      qc.invalidateQueries({ queryKey: ["launchTypes"] });
    };

    const types: LaunchTypeEventType[] = [
      "lt.updated",
      "lt.deleted",
      "lt-day.created",
      "lt-day.updated",
      "lt-day.deleted",
      "lt-card.created",
      "lt-card.updated",
      "lt-card.moved",
      "lt-card.deleted",
    ];
    for (const t of types) source.addEventListener(t, handle as EventListener);
    source.addEventListener("message", handle);

    return () => {
      for (const t of types) source.removeEventListener(t, handle as EventListener);
      source.removeEventListener("message", handle);
      source.close();
    };
  }, [launchTypeId, qc, token]);
}
